// LiveKit unified browser client (ESM)
import * as LK from 'livekit-client';

// --- Global State ---
let room = null;
let currentAudioEls = new Set();
let isConnecting = false;
let audioIOEnabled = false; 
let dotNetRef = null;
let currentSessionToken = null;
let syntheticTranscriptSequence = 0;

const userSegmentBuffer = new Map(); 
let userTurnBuffer = []; 
const agentSegmentBuffer = new Map();
const AUDIO_SINK_ID = 'lk-remote-audio-sink';

// --- Logging ---
const log = (msg, ...args) => console.log(`[LK] 🔷 ${msg}`, ...args);
const warn = (msg, ...args) => console.warn(`[LK] ⚠️ ${msg}`, ...args);
const err = (msg, ...args) => console.error(`[LK] ❌ ${msg}`, ...args);

function textPreview(text, max = 120) {
  if (!text) return '';
  return text.length > max ? `${text.substring(0, max)}...` : text;
}

function voiceTrace(stage, fields = {}) {
  const entry = {
    stage,
    sessionToken: currentSessionToken,
    room: room?.name ?? null,
    roomSid: room?.sid ?? null,
    localIdentity: room?.localParticipant?.identity ?? null,
    timestamp: new Date().toISOString(),
    ...fields,
  };

  console.log('[RTS_VOICE_TRACE]', entry);

  try {
    if (typeof window !== 'undefined' && window.Sentry?.addBreadcrumb) {
      window.Sentry.addBreadcrumb({
        category: 'rts.voice',
        level: 'info',
        message: stage,
        data: entry,
      });
    }
  } catch {
    // Diagnostics should never affect the live voice path.
  }
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    voiceTrace('browser-visibility-change', {
      visibilityState: document.visibilityState,
      hidden: document.hidden,
    });
  });
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', (event) => {
    voiceTrace('browser-page-hide', {
      persisted: event.persisted === true,
    });
  });

  window.addEventListener('pageshow', (event) => {
    voiceTrace('browser-page-show', {
      persisted: event.persisted === true,
    });
  });
}

// --- Interop Helpers ---

function notifyDotNetConnected(connected) {
  if (!dotNetRef) return;
  try {
    log(`Notify .NET Connection: ${connected}`);
    dotNetRef.invokeMethodAsync('OnLiveKitConnectionChanged', !!connected);
  } catch (e) {
    warn('dotnet notify failed', e);
  }
}

function nextSyntheticSegmentId(prefix) {
  syntheticTranscriptSequence += 1;
  return `${prefix}-${Date.now()}-${syntheticTranscriptSequence}`;
}

function notifyDotNetTranscription(
  text,
  isFinal,
  participantIdentity,
  speakerIdentity,
  segmentId = null,
  segmentText = null
) {
  if (!text) return;
  const rawSegmentText = segmentText || text;
  if (!dotNetRef) {
    voiceTrace('browser-dotnet-transcript-skip', {
      reason: 'missing-dotnet-ref',
      participantIdentity,
      speakerIdentity: speakerIdentity || null,
      segmentId,
      isFinal,
      textLen: text.length,
      textPreview: textPreview(text),
    });
    return;
  }
  try {
    log(`Notify .NET Transcription: "${text.substring(0, 50)}${text.length > 50 ? '...' : ''}" [Final: ${isFinal}] [Identity: ${participantIdentity}]`);
    voiceTrace('browser-dotnet-transcript-send', {
      participantIdentity,
      speakerIdentity: speakerIdentity || null,
      segmentId,
      isFinal,
      textLen: text.length,
      textPreview: textPreview(text),
    });
    dotNetRef.invokeMethodAsync(
      'OnTranscriptionReceived',
      text,
      isFinal,
      participantIdentity,
      speakerIdentity || null,
      segmentId,
      rawSegmentText
    )
      .then(() => voiceTrace('browser-dotnet-transcript-ack', {
        participantIdentity,
        speakerIdentity: speakerIdentity || null,
        segmentId,
        isFinal,
        textLen: text.length,
      }))
      .catch(e => {
        warn('dotnet transcription invoke rejected', e);
        voiceTrace('browser-dotnet-transcript-error', {
          participantIdentity,
          speakerIdentity: speakerIdentity || null,
          segmentId,
          isFinal,
          textLen: text.length,
          error: e?.message || String(e),
        });
      });
  } catch (e) {
    warn('dotnet transcription invoke error', e);
    voiceTrace('browser-dotnet-transcript-error', {
      participantIdentity,
      speakerIdentity: speakerIdentity || null,
      segmentId,
      isFinal,
      textLen: text.length,
      error: e?.message || String(e),
    });
  }
}

// --- Transcription-optimized audio (capture + publish) ---
// SINGLE SOURCE OF TRUTH for capture options. C# UI reads via lk_getCaptureOptions and
// writes via lk_setCaptureOptions; it does not store its own defaults.

const PRESET_NAMES = ['speech', 'telephone', 'music'];
const PRESET_MAP = {
  speech: LK.AudioPresets.speech,
  telephone: LK.AudioPresets.telephone,
  music: LK.AudioPresets.music,
};

const CAPTURE_DEFAULTS = {
  sampleRate: 48000,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  voiceIsolation: false,
};

let captureOpts = { ...CAPTURE_DEFAULTS };
let publishPresetName = 'speech';

function getCaptureOptsForSDK() {
  return {
    sampleRate: captureOpts.sampleRate,
    echoCancellation: !!captureOpts.echoCancellation,
    noiseSuppression: !!captureOpts.noiseSuppression,
    autoGainControl: !!captureOpts.autoGainControl,
    voiceIsolation: !!captureOpts.voiceIsolation,
  };
}

function getPublishOptsForSDK() {
  return {
    audioPreset: PRESET_MAP[publishPresetName] || LK.AudioPresets.speech,
  };
}

export function lk_getCaptureOptions() {
  return {
    echoCancellation: !!captureOpts.echoCancellation,
    noiseSuppression: !!captureOpts.noiseSuppression,
    autoGainControl: !!captureOpts.autoGainControl,
    voiceIsolation: !!captureOpts.voiceIsolation,
    sampleRate: captureOpts.sampleRate,
    audioPreset: publishPresetName,
  };
}

export function lk_setCaptureOptions(echoCancellation, noiseSuppression, autoGainControl, voiceIsolation, sampleRate, audioPreset) {
  captureOpts.echoCancellation = !!echoCancellation;
  captureOpts.noiseSuppression = !!noiseSuppression;
  captureOpts.autoGainControl = !!autoGainControl;
  captureOpts.voiceIsolation = !!voiceIsolation;
  if (typeof sampleRate === 'number' && [16000, 44100, 48000].includes(sampleRate)) {
    captureOpts.sampleRate = sampleRate;
  }
  if (typeof audioPreset === 'string' && PRESET_NAMES.includes(audioPreset)) {
    publishPresetName = audioPreset;
  }
  log('Capture options updated', lk_getCaptureOptions());
  voiceTrace('browser-audio-capture-options-updated', lk_getCaptureOptions());

  if (!room) return Promise.resolve(true);
  const pub = room.localParticipant.getTrackPublication(LK.Track.Source.Microphone);
  const micCurrentlyEnabled = pub && !pub.muted;
  if (!micCurrentlyEnabled) return Promise.resolve(true);

  return room.localParticipant.setMicrophoneEnabled(false)
    .then(() => room.localParticipant.setMicrophoneEnabled(true, getCaptureOptsForSDK(), getPublishOptsForSDK()))
    .then(() => {
      voiceTrace('browser-audio-capture-options-reapplied', lk_getCaptureOptions());
      return true;
    })
    .catch((e) => {
      err('Re-apply capture options failed', e);
      voiceTrace('browser-audio-capture-options-error', {
        error: e?.message || String(e),
      });
      return false;
    });
}

// --- Audio & Connection Helpers ---

function cleanupAudioEls() {
  log(`Cleaning up ${currentAudioEls.size} audio elements`);
  voiceTrace('browser-remote-audio-cleanup', {
    elementCount: currentAudioEls.size,
  });
  for (const el of currentAudioEls) {
    try {
      el.pause();
      el.srcObject = null;
      el.remove();
    } catch {}
  }
  currentAudioEls.clear();
}

function getAudioSink() {
  if (typeof document === 'undefined') return null;

  let sink = document.getElementById(AUDIO_SINK_ID);
  if (!sink) {
    sink = document.createElement('div');
    sink.id = AUDIO_SINK_ID;
    sink.setAttribute('aria-hidden', 'true');
    sink.style.position = 'fixed';
    sink.style.width = '1px';
    sink.style.height = '1px';
    sink.style.overflow = 'hidden';
    sink.style.opacity = '0';
    sink.style.pointerEvents = 'none';
    document.body.appendChild(sink);
  }

  return sink;
}

async function startRemoteAudioElement(el) {
  if (room) {
    try {
      await room.startAudio();
      log('Audio context unlocked for remote playback.');
    } catch (e) {
      warn('startAudio blocked/failed for remote track', e);
    }
  }

  try {
    await el.play();
    log('Remote audio playback started.');
  } catch (e) {
    warn('Remote audio play blocked/failed; user gesture may be required', e);
  }
}

function playRemoteAudioTrack(track) {
  if (!audioIOEnabled) {
    log('Audio IO disabled, ignoring remote track');
    return;
  }
  try {
    log(`Attaching remote audio track: ${track.sid}`);
    voiceTrace('browser-remote-audio-track-attach', {
      trackSid: track.sid,
      trackKind: track.kind,
    });
    const el = track.attach();
    el.autoplay = true;
    el.playsInline = true;
    el.controls = false;
    el.muted = false;
    el.dataset.livekitRemoteAudio = 'true';

    const sink = getAudioSink();
    if (sink && !el.isConnected) {
      sink.appendChild(el);
    }

    void startRemoteAudioElement(el);
    currentAudioEls.add(el);
  } catch (e) {
    err('Failed to attach remote audio', e);
    voiceTrace('browser-remote-audio-track-attach-error', {
      trackSid: track?.sid ?? null,
      error: e?.message || String(e),
    });
  }
}

/** Config controls */
export function lk_setAudioIOEnabled(enabled) {
  audioIOEnabled = !!enabled;
  log(`⚙️ AudioIO Enabled: ${audioIOEnabled}`);
  voiceTrace('browser-audio-io-set', {
    enabled: audioIOEnabled,
  });
  if (room && !audioIOEnabled) {
    log('Disabling mic and cleaning up audio due to AudioIO toggle');
    room.localParticipant.setMicrophoneEnabled(false).catch(() => {});
    cleanupAudioEls();
  } else if (room && audioIOEnabled) {
    room.startAudio().catch((e) => warn('startAudio blocked/failed after AudioIO enable', e));
  }
}

export function lk_isConnected() {
  return !!room && room.state === LK.ConnectionState.Connected;
}

// --- MAIN EVENT WIRING ---

function wireRoomLogs(r) {
  // --- GLOBAL EVENT DEBUGGER ---
  // Logs all events except the noisy ones
  const IGNORED_EVENTS = new Set(['audioStreamAcquired', 'audioStreamReleased', 'dataReceived']); 
  
  Object.values(LK.RoomEvent).forEach((eventKey) => {
    if (!IGNORED_EVENTS.has(eventKey)) {
        r.on(eventKey, (...args) => {
            log(`[EVENT] ${eventKey}`, args);
        });
    }
  });

  r
    .on(LK.RoomEvent.Connected, () => {
      log('🔗 Room Connected');
      voiceTrace('browser-livekit-room-connected', {
        connectionState: r.state,
      });
      notifyDotNetConnected(true);
    })
    .on(LK.RoomEvent.Disconnected, (reason) => {
      log(`🔌 Room Disconnected. Reason: ${reason}`);
      voiceTrace('browser-livekit-room-disconnected', {
        reason: reason || null,
      });
      notifyDotNetConnected(false);
      dotNetRef = null;
      userSegmentBuffer.clear();
      userTurnBuffer = [];
      agentSegmentBuffer.clear();
    })
    .on(LK.RoomEvent.Reconnected, () => {
      log('✅ Room Reconnected');
      voiceTrace('browser-livekit-room-reconnected', {
        connectionState: r.state,
      });
      notifyDotNetConnected(true);
    })
    // -- Remote Audio --
    .on(LK.RoomEvent.TrackSubscribed, (track, pub, participant) => {
      log(`🎧 Track Subscribed: ${track.kind} from ${participant?.identity}`);
      voiceTrace('browser-livekit-track-subscribed', {
        participantIdentity: participant?.identity || null,
        trackSid: track?.sid || null,
        trackKind: track?.kind || null,
        source: pub?.source || null,
      });
      if (track.kind === LK.Track.Kind.Audio) {
        playRemoteAudioTrack(track);
        if (userTurnBuffer.length > 0) {
             log(`🔄 AI Audio detected. Clearing User Buffer (Size was: ${userTurnBuffer.length})`);
             userTurnBuffer = [];
        }
      }
    })
    // -- Transcriptions (Speech-to-Text) --
    .on(LK.RoomEvent.TranscriptionReceived, (segments, participant) => {
      const participantIdentity = participant?.identity || 'unknown';
      const isAgent = participantIdentity.toLowerCase().startsWith('agent');
      const type = isAgent ? 'ai' : 'user';

      voiceTrace('browser-livekit-transcription-received', {
        participantIdentity,
        type,
        segmentCount: segments?.length ?? 0,
      });

      segments.forEach((segment) => {
        const segmentId = segment.id;
        const isFinal = segment.final === true;
        const text = (segment.text || '').trim();

        if (!text) return;

        log(`📝 Transcript [${type.toUpperCase()}] [Final: ${isFinal}] ID: ${segmentId} -> "${text}"`);
        voiceTrace('browser-livekit-transcript-segment', {
          participantIdentity,
          type,
          segmentId,
          isFinal,
          textLen: text.length,
          textPreview: textPreview(text),
          userSegmentBufferSize: userSegmentBuffer.size,
          userTurnBufferSize: userTurnBuffer.length,
        });

        if (type === 'user') {
            agentSegmentBuffer.clear();

            // 1. Track current segment
            userSegmentBuffer.set(segmentId, text);

            if (isFinal) {
                userSegmentBuffer.delete(segmentId);
                userTurnBuffer.push(text);
                const accumulatedTurn = userTurnBuffer.join(' ');
                
                log(`📝 User Turn Finalized: "${accumulatedTurn}" (Buffer Size: ${userTurnBuffer.length})`);
                voiceTrace('browser-user-turn-finalized', {
                  participantIdentity,
                  segmentId,
                  textLen: accumulatedTurn.length,
                  textPreview: textPreview(accumulatedTurn),
                  userTurnBufferSize: userTurnBuffer.length,
                });
                notifyDotNetTranscription(
                  accumulatedTurn,
                  true,
                  participantIdentity,
                  null,
                  segmentId,
                  text
                );
            } else {
                const accumulatedFinals = userTurnBuffer.join(' ');
                const displayText = accumulatedFinals ? `${accumulatedFinals} ${text}` : text;

                // log(`📝 User Interim: "${displayText}"`); // Uncomment for verbose interim logs
                voiceTrace('browser-user-turn-interim', {
                  participantIdentity,
                  segmentId,
                  textLen: displayText.length,
                  textPreview: textPreview(displayText),
                  userSegmentBufferSize: userSegmentBuffer.size,
                  userTurnBufferSize: userTurnBuffer.length,
                });
                notifyDotNetTranscription(
                  displayText,
                  false,
                  participantIdentity,
                  null,
                  segmentId,
                  text
                );
            }
        } else {
            // AI Logic
            if (userTurnBuffer.length > 0) {
                log('🔄 AI Transcription detected. Resetting User Buffer.');
                userTurnBuffer = [];
            }

            if (isFinal) {
                agentSegmentBuffer.set(segmentId, text);
            }
            const accumulatedFinals = Array.from(agentSegmentBuffer.values()).join(' ');
            const displayText = isFinal
                ? accumulatedFinals
                : (accumulatedFinals ? `${accumulatedFinals} ${text}` : text);

            voiceTrace('browser-ai-transcript', {
              participantIdentity,
              segmentId,
              isFinal,
              textLen: displayText.length,
              textPreview: textPreview(displayText),
            });
            notifyDotNetTranscription(
              displayText,
              isFinal,
              participantIdentity,
              null,
              segmentId,
              text
            );
        }
      });
    })
    // -- Data Channel (Chat) --
    .on(LK.RoomEvent.DataReceived, (payload, participant, kind, topic) => {
      if (topic !== 'lk.chat') {
          log(`📨 Data Received (Ignored Topic: ${topic})`);
          return;
      }

      try {
        const rawString = new TextDecoder().decode(payload);
        const senderIdentity = participant?.identity || 'user';
        
        log(`📨 Chat Data Received from ${senderIdentity}: ${rawString}`);

        let textToSend = rawString;
        let speakerOverride = null;

        if (rawString.trim().startsWith('{')) {
            try {
                const json = JSON.parse(rawString);
                if (json.text) {
                    textToSend = json.text;
                    if (json.speakerId) speakerOverride = json.speakerId;
                }
            } catch { }
        }

        if (textToSend) {
            log(`💬 Processing Chat Message: "${textToSend}"`);
            voiceTrace('browser-chat-message-received', {
              senderIdentity,
              speakerOverride,
              textLen: textToSend.length,
              textPreview: textPreview(textToSend),
            });
            userTurnBuffer = [];
            userSegmentBuffer.clear();
            agentSegmentBuffer.clear();
            notifyDotNetTranscription(
              textToSend,
              true,
              senderIdentity,
              speakerOverride,
              nextSyntheticSegmentId('remote-chat'),
              textToSend
            );
        }
      } catch (e) {
        warn('Chat decode error', e);
      }
    });
}

/** Connect */
export async function lk_connect(url, token, opts = {}) {
  currentSessionToken = opts.sessionToken || currentSessionToken || null;
  log('🚀 lk_connect initiated', { url: url, audioIO: opts.audioIOEnabled });
  voiceTrace('browser-livekit-connect-start', {
    urlHost: (() => {
      try { return new URL(url).host; } catch { return null; }
    })(),
    audioIOEnabled: !!opts.audioIOEnabled,
    autoEnableMic: !!(opts.autoEnableMic ?? true),
    hasDotNetRef: !!opts.dotNetRef,
  });
  if (room) {
    // A second concurrent lk_connect() (e.g. a double-click on "Start Simulation" racing two
    // StartConversation calls, see WEB-1684) used to report success here without actually
    // connecting to the new room, silently leaving the caller's C# state pointed at a room the
    // browser was never connected to. Report failure instead so the caller can react correctly.
    warn('Room already exists, aborting connect');
    voiceTrace('browser-livekit-connect-skip', {
      reason: 'room-already-exists',
      connectionState: room.state,
    });
    return false;
  }

  dotNetRef = opts.dotNetRef || null;
  
  if (typeof opts.audioIOEnabled === 'boolean') {
    audioIOEnabled = opts.audioIOEnabled;
  }
  
  const wantAutoMic = !!(opts.autoEnableMic ?? true);

  try {
    isConnecting = true;
    
    userSegmentBuffer.clear();
    userTurnBuffer = [];

    room = new LK.Room({
      adaptiveStream: true,
      dynacast: true,
      audioCaptureDefaults: getCaptureOptsForSDK(),
      publishDefaults: getPublishOptsForSDK(),
    });
    wireRoomLogs(room);

    await room.connect(url, token);
    log(`✅ Connected successfully. SID: ${room.sid}`);
    voiceTrace('browser-livekit-connect-success', {
      connectionState: room.state,
    });
    notifyDotNetConnected(true);

    if (audioIOEnabled) {
      try {
          log('Attempting to start audio...');
          await room.startAudio();
          log('Audio started.');
          voiceTrace('browser-livekit-start-audio-success');
      } catch(e) {
          warn('startAudio blocked/failed', e);
          voiceTrace('browser-livekit-start-audio-error', {
            error: e?.message || String(e),
          });
      }

      if (wantAutoMic) await lk_setMicEnabled(true);
    }

    isConnecting = false;
    return true;
  } catch (e) {
    isConnecting = false;
    err('Connection failed', e);
    voiceTrace('browser-livekit-connect-error', {
      error: e?.message || String(e),
    });
    notifyDotNetConnected(false);
    try { await lk_disconnect(); } catch {}
    return false;
  }
}

export async function lk_disconnect() {
  log('🛑 Disconnecting...');
  voiceTrace('browser-livekit-disconnect-start', {
    hadRoom: !!room,
    connectionState: room?.state ?? null,
  });
  try {
    if (room) {
      cleanupAudioEls();
      await room.disconnect();
    }
  } catch (e) {
    warn('Disconnect error', e);
    voiceTrace('browser-livekit-disconnect-error', {
      error: e?.message || String(e),
    });
  } finally {
    room = null;
    isConnecting = false;
    notifyDotNetConnected(false);
    dotNetRef = null;
    userSegmentBuffer.clear();
    userTurnBuffer = [];
    agentSegmentBuffer.clear();
    log('🛑 Disconnected and state reset.');
    voiceTrace('browser-livekit-disconnect-complete');
    currentSessionToken = null;
  }
}

export async function lk_setMicEnabled(enabled) {
  if (!room) {
      warn('Cannot set mic: Room is null');
      voiceTrace('browser-mic-set-skip', {
        enabled: !!enabled,
        reason: 'room-null',
      });
      return false;
  }
  try {
    log(`🎤 Setting Mic Enabled: ${enabled}`);
    voiceTrace('browser-mic-set-start', {
      enabled: !!enabled,
      capture: getCaptureOptsForSDK(),
      publishPresetName,
    });
    await room.localParticipant.setMicrophoneEnabled(
      enabled,
      getCaptureOptsForSDK(),
      getPublishOptsForSDK()
    );
    const pub = room.localParticipant.getTrackPublication(LK.Track.Source.Microphone);
    voiceTrace('browser-mic-set-success', {
      enabled: !!enabled,
      muted: pub?.muted ?? null,
      trackSid: pub?.trackSid ?? null,
    });
    return true;
  } catch (e) {
    err('Mic toggle error', e);
    voiceTrace('browser-mic-set-error', {
      enabled: !!enabled,
      error: e?.message || String(e),
    });
    return false;
  }
}

export async function lk_sendText(payload) {
  if (!room) {
      warn('Cannot send text: Room is null');
      return false;
  }
  try {
    let msg = "";
    let localSpeaker = null;
    let textToEcho = "";

    if (typeof payload === 'object') {
        msg = JSON.stringify(payload);
        localSpeaker = payload.speakerId;
        textToEcho = payload.text;
    } else {
        msg = payload;
        textToEcho = payload;
    }

    log(`📤 Sending Text via Data Channel: "${textToEcho}"`);
    voiceTrace('browser-text-message-send', {
      localSpeaker,
      textLen: textToEcho?.length ?? 0,
      textPreview: textPreview(textToEcho),
    });

    const data = new TextEncoder().encode(msg);
    await room.localParticipant.publishData(data, { topic: 'lk.chat', reliable: true });
    
    userTurnBuffer = [];
    userSegmentBuffer.clear();
    agentSegmentBuffer.clear();

    const identity = room.localParticipant?.identity ?? 'local-user';
    notifyDotNetTranscription(
      textToEcho,
      true,
      identity,
      localSpeaker,
      nextSyntheticSegmentId('local-chat'),
      textToEcho
    );
    
    return true;
  } catch (e) {
    err('sendText error', e);
    return false;
  }
}

if (typeof window !== 'undefined') {
  window.LKUnified = {
    lk_connect, lk_disconnect, lk_setMicEnabled, lk_setAudioIOEnabled, lk_isConnected, lk_sendText,
    lk_getCaptureOptions, lk_setCaptureOptions
  };
}
